import { gql } from 'graphql-tag';

// =============================================================================
// Coaching — GraphQL operations against the Nexus backend
// =============================================================================
// Three layers:
//   1. Persona scheduling — a persona (alumni/team/sponsor/creator) exposes a
//      booking surface. Tiers: 'connected' (Calendly OAuth, prefill+lock),
//      'link' (a raw scheduling URL), or 'none'.
//   2. Program coaching policy — a program declares whether coaching is enabled,
//      whether it's required, the cadence, and expected calls per participant.
//   3. Participant coaching status — per-participant satisfaction of the policy
//      (required, cadence, satisfied, state, next call, sessions count) plus the
//      participant's own coaching sessions.
//
// These mirror the Nexus backend being built in parallel. Inline gql only —
// generated types are intentionally out of scope here.
// =============================================================================

// ------- Queries -------

export const PERSONA_SCHEDULING_QUERY = gql`
  query PersonaScheduling($personaId: String!) {
    personaScheduling(personaId: $personaId) {
      mode
      provider
      bookingUrl
      label
      enabled
      connectionStatus
      schedulingUrl
    }
  }
`;

export const PROGRAM_COACHING_POLICY_QUERY = gql`
  query ProgramCoachingPolicy($programId: String!) {
    programCoachingPolicy(programId: $programId) {
      enabled
      required
      allowBookLater
      cadence
      expectedCallsPerParticipant
      maxCallsPerPeriod
      maxCallsPeriodUnit
      inAppNudge
      smsNudge
      allowSelfAttest
    }
  }
`;

/**
 * coachingModuleState(programId) — the Program-Modules framework view of coaching
 * (Program-Modules epic, Wave 4). Returns the GraphQLJSON scalar (NO sub-selection),
 * mirroring placesModuleState: { enabled, policy, state }. `deriveState` runs
 * SERVER-side, so the FE nudge is a pure pass-through of `state`. Distinct from
 * `coachingStatusForParticipant` (the legacy pill's status source) — this folds the
 * policy's rigidity/inAppNudge/smsNudge into one runner-consumable payload.
 */
export const COACHING_MODULE_STATE_QUERY = gql`
  query CoachingModuleState($programId: String!) {
    coachingModuleState(programId: $programId)
  }
`;

export const MY_COACHING_SESSIONS_QUERY = gql`
  query MyCoachingSessions {
    myCoachingSessions {
      id
      status
      scheduledStart
      scheduledEnd
      joinUrl
      personaId
    }
  }
`;

export const CALENDLY_EVENT_TYPES_QUERY = gql`
  query CalendlyEventTypes($personaId: String!) {
    coachingEventTypes(personaId: $personaId) {
      uri
      name
      schedulingUrl
      active
      durationMinutes
    }
  }
`;

export const COACHING_STATUS_FOR_PARTICIPANT_QUERY = gql`
  query CoachingStatusForParticipant($programId: String!) {
    coachingStatusForParticipant(programId: $programId) {
      required
      cadence
      satisfied
      state
      nextCall
      lastCall
      sessionsCount
      coachPersonaId
      coachName
      coachPhotoUrl
      bookingLimit {
        enforced
        max
        periodUnit
        usedInWindow
        atLimit
        nextAvailableAt
      }
    }
  }
`;

// ------- Mutations -------

export const SET_PERSONA_SCHEDULING_MUTATION = gql`
  mutation SetPersonaScheduling($input: SetPersonaSchedulingInput!) {
    setPersonaScheduling(input: $input) {
      mode
      provider
      bookingUrl
      label
      enabled
      connectionStatus
      schedulingUrl
    }
  }
`;

export const CONNECT_CALENDLY_MUTATION = gql`
  mutation ConnectCalendly($personaId: String!) {
    connectCalendly(personaId: $personaId) {
      mode
      provider
      bookingUrl
      label
      enabled
      connectionStatus
      schedulingUrl
    }
  }
`;

export const DISCONNECT_SCHEDULING_MUTATION = gql`
  mutation DisconnectScheduling($personaId: String!) {
    disconnectScheduling(personaId: $personaId) {
      mode
      provider
      bookingUrl
      label
      enabled
      connectionStatus
      schedulingUrl
    }
  }
`;

export const SET_COACHING_EVENT_TYPE_MUTATION = gql`
  mutation SetCoachingEventType(
    $personaId: String!
    $eventTypeUri: String!
    $schedulingUrl: String!
  ) {
    setCoachingEventType(
      personaId: $personaId
      eventTypeUri: $eventTypeUri
      schedulingUrl: $schedulingUrl
    ) {
      mode
      provider
      bookingUrl
      label
      enabled
      connectionStatus
      schedulingUrl
    }
  }
`;

/**
 * acknowledgeCoachingBooking(programId) — durably records that the participant has
 * booked / self-attested their required 1:1 (Program-Modules coaching fix A). Stamps
 * Student.aipm.coachingAcknowledgedAt server-side so `satisfied` reads true even with
 * ZERO CoachingSessions (the link / self-attest tier where the Calendly webhook isn't
 * wired) — which flips the coaching module off 'due' and stops the re-pop. Idempotent
 * (first-write-wins). Returns the acknowledged timestamp (ISO). Gated by
 * AIPM_COACHING_ENABLED on the backend.
 */
export const ACKNOWLEDGE_COACHING_BOOKING_MUTATION = gql`
  mutation AcknowledgeCoachingBooking($programId: String!) {
    acknowledgeCoachingBooking(programId: $programId)
  }
`;

export const SET_PROGRAM_COACHING_POLICY_MUTATION = gql`
  mutation SetProgramCoachingPolicy($input: SetProgramCoachingPolicyInput!) {
    setProgramCoachingPolicy(input: $input) {
      enabled
      required
      allowBookLater
      cadence
      expectedCallsPerParticipant
      maxCallsPerPeriod
      maxCallsPeriodUnit
      inAppNudge
      smsNudge
      allowSelfAttest
    }
  }
`;

// ------- Client-side shapes (mirror the backend DTOs) -------

/** How a persona exposes booking. */
export type SchedulingMode = 'connected' | 'link' | 'none';

/**
 * Scheduling provider adapter key. Mirrors the backend exactly: the
 * SetPersonaSchedulingInput.provider field and PersonaSchedulingType.provider
 * are constrained to this set (class-validator @IsIn on the backend).
 */
export type SchedulingProvider = 'calendly' | 'cal' | 'google' | 'custom';

/** OAuth connection status for the 'connected' (Calendly) tier. */
export type SchedulingConnectionStatus = 'connected' | 'disconnected' | 'pending' | 'error';

export interface PersonaScheduling {
  mode: SchedulingMode;
  provider: SchedulingProvider | null;
  /** Raw scheduling URL for the 'link' tier (iframe / external "Book a call"). */
  bookingUrl: string | null;
  label: string | null;
  enabled: boolean;
  /** Only meaningful for the 'connected' tier. */
  connectionStatus: SchedulingConnectionStatus | null;
  /** Calendly scheduling URL for the 'connected' tier (official widget). */
  schedulingUrl: string | null;
}

/**
 * A Calendly event type (scheduling link) exposed by a connected persona's
 * Calendly account. Mirrors the backend `calendlyEventTypes` query exactly.
 * The creator picks one of these to be the persona's coaching booking link.
 */
export interface CalendlyEventType {
  /** Calendly's canonical event-type URI (stable id). */
  uri: string;
  /** Display name, e.g. "Coaching". */
  name: string;
  /** Public scheduling URL, e.g. calendly.com/pilotcity/coaching. */
  schedulingUrl: string;
  /** Whether this event type is currently active/bookable. */
  active: boolean;
  /** Call length in minutes. */
  durationMinutes: number;
}

export interface SetPersonaSchedulingInput {
  personaId: string;
  /** Provider adapter key (required): 'calendly' | 'cal' | 'google' | 'custom'. */
  provider: SchedulingProvider;
  bookingUrl?: string;
  label?: string;
  enabled?: boolean;
}

/** Rolling window unit for the per-period booking cap. */
export type BookingPeriodUnit = 'day' | 'week' | 'month';

export interface ProgramCoachingPolicy {
  enabled: boolean;
  required: boolean;
  allowBookLater: boolean;
  cadence: string | null;
  expectedCallsPerParticipant: number | null;
  /** Per-period booking cap (ceiling). null = "No Limit"; a real cap is >= 1. */
  maxCallsPerPeriod?: number | null;
  /** Period unit for maxCallsPerPeriod. */
  maxCallsPeriodUnit?: BookingPeriodUnit | null;
  /** In-app nudge channel (default ON). Drives the runner's auto-open. */
  inAppNudge?: boolean;
  /** SMS nudge channel (default OFF). Sent via the AIPM outreach engine. */
  smsNudge?: boolean;
  /**
   * Whether participants may self-confirm they booked (the "I've booked" checkbox)
   * instead of actually scheduling. Default false. Only effective when a scheduler
   * is connected — link-tier coaches always allow self-confirm.
   */
  allowSelfAttest?: boolean;
}

export interface SetProgramCoachingPolicyInput {
  programId: string;
  enabled: boolean;
  required: boolean;
  allowBookLater: boolean;
  cadence?: string;
  expectedCallsPerParticipant?: number;
  /** Per-period booking cap (omit / null = "No Limit"; a real cap is >= 1). */
  maxCallsPerPeriod?: number | null;
  /** Period unit for maxCallsPerPeriod. */
  maxCallsPeriodUnit?: BookingPeriodUnit | null;
  /** In-app nudge channel toggle (default ON). */
  inAppNudge?: boolean;
  /** SMS nudge channel toggle (default OFF). */
  smsNudge?: boolean;
  /** Allow participants to self-confirm they booked (default OFF). */
  allowSelfAttest?: boolean;
}

/**
 * Per-period booking cap status for the participant (soft gate). `enforced` is
 * true only for Calendly-connected coaching; link-tier is advisory.
 */
export interface CoachingBookingLimit {
  enforced: boolean;
  max: number;
  periodUnit: BookingPeriodUnit;
  usedInWindow: number;
  atLimit: boolean;
  nextAvailableAt?: string | null;
}

// ------- Program-Modules framework shapes (mirror placesModuleState) -------

/**
 * The resolved coaching policy as seen by the module framework. Mirrors PlacesPolicy:
 * `rigidity` = required ? (allowBookLater ? 'soft' : 'hard') : 'soft' (resolved
 * SERVER-side), plus the two nudge-channel toggles. Open fields (cadence/allowBookLater/
 * expectedCallsPerParticipant) are carried for display + parity with the policy query.
 */
export interface CoachingModulePolicy {
  enabled: boolean;
  required?: boolean;
  rigidity?: 'soft' | 'hard';
  inAppNudge?: boolean;
  smsNudge?: boolean;
  cadence?: string | null;
  allowBookLater?: boolean;
  expectedCallsPerParticipant?: number | null;
}

/** Server-derived nudge state. Mirrors the ModuleNudgeState union. */
export type CoachingModuleNudgeState = 'idle' | 'due' | 'overdue';

/**
 * The coach (persona) a program's coaching is scoped to. Additive on
 * coachingModuleState — coaching is a relationship with a COACH, so one call
 * with them satisfies every program that coach covers.
 */
export interface CoachingModuleCoach {
  personaId: string;
  name: string;
  photoUrl?: string | null;
}

/**
 * One of the participant's ACTIVE programs this coach covers. Additive on
 * coachingModuleState — drives the "one call covers all N programs" disclosure
 * and the "already covered" confirm card's "Covers: …" line.
 */
export interface CoachingCoveredProgram {
  programId: string;
  name: string;
}

/**
 * coachingModuleState payload. Mirrors PlacesModuleState (minus owedWindow — coaching's
 * owed signal is the cadence `state` itself). A pending first REQUIRED call surfaces as
 * `state: 'due'` server-side.
 */
export interface CoachingModuleState {
  enabled: boolean;
  policy: CoachingModulePolicy;
  state: CoachingModuleNudgeState;
  /**
   * The participant's next UPCOMING booked session (ISO string), or null when none.
   * Additive (older backends omit it → undefined). The onboarding coaching phase SKIPS
   * when this is present (already booked). Ignored by the runner (reads only `state`).
   */
  nextCall?: string | null;
  /**
   * Whether the participant has EVER booked a coaching session (sticky — a past call
   * keeps this true). Additive. Used together with `state` for the onboarding skip
   * ("state==='idle' because satisfied"). Ignored by the runner.
   */
  satisfied?: boolean;
  /**
   * The coach this program's coaching is scoped to (persona). Additive — powers the
   * onboarding "already covered" confirm card (avatar + name). null when unresolved.
   */
  coach?: CoachingModuleCoach | null;
  /**
   * The participant's ACTIVE programs this ONE coach covers. Additive. length > 1 ⇒
   * the coach is shared across multiple of the participant's programs, so a single
   * call covers them all (disclosed in onboarding, never silently skipped).
   */
  programsCovered?: CoachingCoveredProgram[];
  /**
   * Enriched, prefilled Calendly booking URL (utm/program list/name/email baked in).
   * Additive — PREFER this over the raw persona scheduling URL when present.
   */
  bookingUrl?: string | null;
}

export type CoachingSessionStatus = 'scheduled' | 'completed' | 'canceled' | 'rescheduled';

export interface CoachingSession {
  id: string;
  status: CoachingSessionStatus;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  joinUrl: string | null;
  personaId: string | null;
}

/** Cadence state. Mirrors the backend CoachingStatusType.state exactly. */
export type CoachingState = 'idle' | 'due' | 'overdue';

export interface CoachingStatusForParticipant {
  required: boolean;
  cadence: string | null;
  satisfied: boolean;
  state: CoachingState;
  nextCall: string | null;
  lastCall: string | null;
  sessionsCount: number;
  /** The participant's coach persona id (booked coach, else program default). */
  coachPersonaId: string | null;
  /** Coach display name. */
  coachName: string | null;
  /** Coach avatar URL. */
  coachPhotoUrl: string | null;
  /** Per-period booking cap status (null when no cap configured). */
  bookingLimit?: CoachingBookingLimit | null;
}
